import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';

const MensCircle = () => {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleGoBack = () => {
    navigate('/#upcoming-experiences');
  };

  return (
    <>
      <SEO
        title="Men's Circle - Sacred Brotherhood & Authentic Connection"
        description="Bi-weekly men's circle for authentic connection and growth. A judgment-free space for brotherhood, vulnerability, and personal development."
        canonicalUrl="https://mysticforest.club/experiences/mens-circle"
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
      
      {/* Go Back Button */}
      <div className="pt-20 pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Experiences
          </Button>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative py-32">
        <div className="absolute inset-0">
          <img 
            src="/lovable-uploads/19cf4e67-6be1-42a4-8cb0-c18c1921c8cf.png" 
            alt="Men's Circle"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
            Men's Circle
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            A sacred brotherhood for authentic connection and growth
          </p>
          <Button size="lg" className="px-8 py-4 text-lg" onClick={handleBooking}>
            Join the Circle
          </Button>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-16 bg-card">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Calendar className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">When</h3>
              <p className="text-muted-foreground">Every 2nd Saturday</p>
              <p className="text-sm text-muted-foreground">Next: March 9, 2024</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Where</h3>
              <p className="text-muted-foreground">Forest Sacred Grove</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">For Whom</h3>
              <p className="text-muted-foreground">Men 25+ seeking depth</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Duration</h3>
              <p className="text-muted-foreground">3 Hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-8 text-center">
            Reclaim Your Authentic Masculine
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground">
            <p className="mb-6">
              In a world that often leaves men feeling isolated and disconnected from their authentic selves, our Men's Circle provides a powerful space for brotherhood, vulnerability, and growth. Here, masks come off and real connection begins.
            </p>
            <p className="mb-6">
              Led by experienced facilitators, these gatherings create a container where men can explore their deepest challenges, celebrate their victories, and support each other in becoming the leaders, fathers, and partners they're meant to be.
            </p>
            <p className="mb-6">
              This is not therapy or coaching – it's brotherhood in its purest form, where ancient wisdom meets modern challenges.
            </p>
          </div>
        </div>
      </section>

      {/* What We Explore */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            What We Explore Together
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Sacred Sharing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Authentic communication in a judgment-free zone where your truth is honored and witnessed.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Shadow Work</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Explore the hidden aspects of yourself that hold you back from living your fullest expression.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Purposeful Living</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Discover and embody your deeper purpose beyond societal expectations and conditioning.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Emotional Intelligence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Learn to navigate and express emotions in healthy, powerful ways that serve your relationships.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif font-light mb-8">Investment</h2>
          <Card className="p-8">
            <div className="text-4xl font-semibold mb-4">$85</div>
            <p className="text-muted-foreground mb-6">Per session</p>
            <div className="text-sm text-muted-foreground mb-8">
              <p>Includes light refreshments and materials</p>
              <p>Monthly commitment recommended for deeper integration</p>
            </div>
            <Button size="lg" className="w-full mb-4" onClick={handleBooking}>
              Join Next Circle
            </Button>
            <p className="text-xs text-muted-foreground">
              First session is free for newcomers
            </p>
          </Card>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
};

export default MensCircle;